/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, Calendar, ChevronRight, AlertTriangle, ClipboardList, Briefcase, Trash2 } from 'lucide-react';
import TecnicoLayout from '../components/TecnicoLayout';
import AssegnaLavoroModal from '../components/AssegnaLavoroModal';
import AnnullaTimerModal from '../components/AnnullaTimerModal';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../api/dashboard';
import { sessioniAPI } from '../api/sessioni';
import { useElapsed, fmtElapsed } from '../hooks/useTimer';
import { fmtDurata, fmtDurataOre } from '../utils/durata';
import { fmtOra } from '../utils/date';

const ACCENT = '#10B981';
const STATO_LABELS = { in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato' };
const PRIO = { urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' }, alta: { bg: '#FED7AA', color: '#9A3412', label: 'Alta' }, normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' }, bassa: { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' } };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [assegnaModal, setAssegnaModal] = useState(false);
  const [annullaModal, setAnnullaModal] = useState(false);
  const [msg, setMsg] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['dashboard-tecnico'], queryFn: () => dashboardAPI.tecnico() });
  const { data: sessioneAttiva } = useQuery({
    queryKey: ['sessione-attiva'],
    queryFn: () => sessioniAPI.attiva(),
    refetchInterval: 60000,
  });

  const elapsed = useElapsed(sessioneAttiva?.inizio);
  const ore = elapsed / 3600;
  const sessioneLibera = sessioneAttiva && !sessioneAttiva.intervento_id;

  const startLiberoMut = useMutation({
    mutationFn: () => sessioniAPI.start(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessione-attiva'] }),
    onError: (e) => setMsg(e.response?.data?.error || 'Errore avvio timer'),
  });

  if (isLoading) return <TecnicoLayout title="Oggi"><div style={styles.loading}>Caricamento...</div></TecnicoLayout>;

  return (
    <TecnicoLayout>
      <div style={styles.welcome}>
        <h1 style={{ margin: 0 }}>Ciao {cap(user?.nome)} 👋</h1>
        <p style={{ color: '#6B7280', margin: '0.3rem 0 0' }}>Ecco i tuoi lavori.</p>
      </div>

      {msg && <div style={styles.msgBox}>{msg}</div>}
      {okMsg && <div style={styles.okBox}>{okMsg}</div>}

      {/* Timer attivo */}
      {sessioneAttiva ? (
        <>
          <div style={styles.timerCard} onClick={() => sessioneLibera ? setAssegnaModal(true) : navigate(`/interventi/${sessioneAttiva.intervento_id}`)}>
            <div style={styles.timerPulse} />
            <div style={{ flex: 1 }}>
              <div style={styles.timerLabel}>⏱️ {sessioneLibera ? 'Lavoro libero in corso' : 'Timer attivo'}</div>
              <div style={styles.timerCode}>{sessioneLibera ? 'Tocca per assegnare a un cliente o segnarlo come interno' : `${sessioneAttiva.codice} · ${sessioneAttiva.titolo}`}</div>
              <div style={styles.timerTime}>{fmtElapsed(elapsed)}</div>
              <div style={styles.timerStart}>Iniziato alle {fmtOra(sessioneAttiva.inizio)}</div>
            </div>
            <ChevronRight size={22} color="white" />
          </div>
          <button onClick={() => setAnnullaModal(true)} style={styles.annullaBtn}>
            <Trash2 size={14} /> Avviato per errore? Annulla senza conteggiare
          </button>
        </>
      ) : (
        <>
          <div style={styles.noTimer}>
            <Clock size={20} color="#6B7280" />
            <span>Nessun timer attivo. Apri un intervento per iniziare.</span>
          </div>
          <button onClick={() => startLiberoMut.mutate()} disabled={startLiberoMut.isPending} style={styles.liberoBtn}>
            <Briefcase size={17} /> {startLiberoMut.isPending ? 'Avvio...' : 'Inizia lavoro libero'}
          </button>
        </>
      )}

      {annullaModal && sessioneAttiva && (
        <AnnullaTimerModal
          sessione={sessioneAttiva}
          onClose={() => setAnnullaModal(false)}
          onDone={() => {
            setAnnullaModal(false);
            setOkMsg('Timer annullato: quelle ore non sono state registrate.');
            qc.invalidateQueries({ queryKey: ['sessione-attiva'] });
            qc.invalidateQueries({ queryKey: ['dashboard-tecnico'] });
          }}
        />
      )}

      {assegnaModal && sessioneLibera && (
        <AssegnaLavoroModal
          sessione={sessioneAttiva}
          onClose={() => setAssegnaModal(false)}
          onDone={(result) => {
            setAssegnaModal(false);
            qc.invalidateQueries({ queryKey: ['sessione-attiva'] });
            qc.invalidateQueries({ queryKey: ['dashboard-tecnico'] });
            if (result?.intervento_id) navigate(`/interventi/${result.intervento_id}`);
          }}
        />
      )}

      {/* Promemoria: gentile dopo 2 ore, piu' esplicito a fine giornata */}
      {sessioneAttiva && ore >= 12 ? (
        <div style={{ ...styles.reminder, ...styles.reminderForte }}>
          <AlertTriangle size={18} color="#991B1B" />
          <span>
            Il timer è attivo da <strong>{fmtDurata(elapsed)}</strong>. Se hai finito, fermalo e semmai
            fallo ripartire domani: una sessione non dovrebbe coprire più giorni. Se era partito per
            errore, usa "Annulla" qui sopra.
          </span>
        </div>
      ) : sessioneAttiva && ore >= 2 ? (
        <div style={styles.reminder}>
          <AlertTriangle size={18} color="#92400E" />
          <span>Il timer è attivo da <strong>{fmtDurata(elapsed)}</strong>. Ricordati di fermarlo quando hai finito!</span>
        </div>
      ) : null}

      {/* KPI */}
      <div style={styles.kpiRow}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiVal}>{data?.assegnati_attivi ?? 0}</div>
          <div style={styles.kpiLabel}>Lavori attivi</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiVal}>{data?.completati_mese ?? 0}</div>
          <div style={styles.kpiLabel}>Completati (mese)</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValDurata}>{fmtDurataOre(data?.ore_mese ?? 0)}</div>
          <div style={styles.kpiLabel}>Ore (mese)</div>
        </div>
      </div>

      {/* Appuntamenti oggi */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}><Calendar size={18} /> Oggi</h3>
        {!data?.appuntamenti_oggi || data.appuntamenti_oggi.length === 0 ? (
          <div style={styles.empty}>Nessun appuntamento programmato per oggi.</div>
        ) : (
          <div style={styles.list}>
            {data.appuntamenti_oggi.map((i) => (
              <InterventoRow key={i.id} i={i} onClick={() => navigate(`/interventi/${i.id}`)} showTime />
            ))}
          </div>
        )}
      </div>

      {/* Prossimi lavori */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><ClipboardList size={18} /> Prossimi lavori</h3>
          <button onClick={() => navigate('/interventi')} style={styles.linkBtn}>Vedi tutti →</button>
        </div>
        {!data?.prossimi || data.prossimi.length === 0 ? (
          <div style={styles.empty}>Nessun lavoro in programma.</div>
        ) : (
          <div style={styles.list}>
            {data.prossimi.map((i) => (
              <InterventoRow key={i.id} i={i} onClick={() => navigate(`/interventi/${i.id}`)} />
            ))}
          </div>
        )}
      </div>
    </TecnicoLayout>
  );
}

function InterventoRow({ i, onClick, showTime }) {
  const p = PRIO[i.priorita] || PRIO.normale;
  return (
    <div style={styles.card} onClick={onClick}>
      {showTime && i.data_appuntamento && (
        <div style={styles.timeChip}>{fmtOra(i.data_appuntamento)}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.cardHead}>
          <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>{STATO_LABELS[i.stato]}</span>
          <span style={{ ...styles.prioBadge, backgroundColor: p.bg, color: p.color }}>{p.label}</span>
          <code style={styles.code}>{i.codice}</code>
        </div>
        <div style={{ fontWeight: 600, marginTop: '0.3rem' }}>{i.titolo}</div>
        <div style={styles.meta}>
          {i.cliente_nome && <>{i.cliente_nome} {i.cliente_cognome}</>}
          {i.citta && <> · <MapPin size={12} /> {i.citta}</>}
        </div>
      </div>
      <ChevronRight size={18} color="#9CA3AF" />
    </div>
  );
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  welcome: { marginBottom: '1.25rem' },
  timerCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1.25rem', background: 'linear-gradient(135deg, #10B981, #059669)', borderRadius: '14px', color: 'white', cursor: 'pointer', marginBottom: '1rem', position: 'relative', overflow: 'hidden' },
  timerPulse: { position: 'absolute', top: '1rem', right: '3rem', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#FCA5A5', boxShadow: '0 0 0 rgba(252,165,165,0.6)', animation: 'none' },
  timerLabel: { fontSize: '0.82rem', opacity: 0.9, fontWeight: 600 },
  timerCode: { fontSize: '0.9rem', opacity: 0.95, marginTop: '0.2rem' },
  timerTime: { fontSize: '2.4rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', margin: '0.3rem 0', letterSpacing: '0.02em' },
  timerStart: { fontSize: '0.8rem', opacity: 0.85 },
  noTimer: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '1rem 1.25rem', backgroundColor: 'white', borderRadius: '12px', color: '#6B7280', fontSize: '0.9rem', marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  liberoBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.85rem', backgroundColor: 'white', color: ACCENT, border: `2px dashed ${ACCENT}`, borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem', marginBottom: '1.25rem' },
  annullaBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', width: '100%', padding: '0.5rem', marginBottom: '1rem', backgroundColor: 'transparent', color: '#6B7280', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.82rem', textDecoration: 'underline' },
  msgBox: { padding: '0.75rem 1rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
  okBox: { padding: '0.75rem 1rem', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
  reminderForte: { backgroundColor: '#FEE2E2', color: '#991B1B' },
  reminder: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.85rem 1rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '10px', fontSize: '0.88rem', marginBottom: '1rem' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', marginBottom: '1.25rem' },
  kpiCard: { padding: '1rem 0.75rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' },
  kpiVal: { fontSize: '1.5rem', fontWeight: 700, color: ACCENT },
  kpiValDurata: { fontSize: '1.2rem', fontWeight: 700, color: ACCENT, whiteSpace: 'nowrap' },
  kpiLabel: { fontSize: '0.72rem', color: '#6B7280', marginTop: '0.2rem' },
  section: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.25rem' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  sectionTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  linkBtn: { background: 'none', border: 'none', color: ACCENT, fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  card: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem', backgroundColor: '#F9FAFB', borderRadius: '10px', cursor: 'pointer' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700 },
  prioBadge: { padding: '0.18rem 0.5rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600 },
  code: { backgroundColor: '#E5E7EB', padding: '0.12rem 0.4rem', borderRadius: '4px', fontSize: '0.74rem', fontFamily: 'ui-monospace, monospace' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: '#6B7280', marginTop: '0.3rem' },
  timeChip: { padding: '0.4rem 0.65rem', backgroundColor: ACCENT + '20', color: ACCENT, borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 },
  empty: { padding: '1.5rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.88rem' },
};
